import { metersQueryOptions } from '@/src/features/Messstellen/useMeters.hook'
import type { SeriesTypeEnum } from '@/src/features/analytics/enums/seriesType.enum'
import { getLabelledTimeSeries } from '@/src/features/analytics/utils'
import { createContext, useContext, useEffect, useState } from 'react'
import { API } from '../api/api'
import { queryClient } from '../api/queryClient'
import { useLocationsContext } from './LocationsContext'

export interface ISeries {
  id: string
  title: string
  name: string
  type: SeriesTypeEnum
  locationId: number
  buildingId: number
  locationName: string
  groupId: number
  energyType: string
  resolution: {
    enum: string
    label: string
    minutes: number
  }
  aggregationType: string
  unit: string
  label: string // similar to name but will have appended locationName and/or buildingName if there are repeated names
  minDate: string
  maxDate: string
  isVirtual?: boolean // added to handle virtual meters
  isDefault?: boolean
  deviceType?: 'consumption' | 'generation'
  source?: string
}

const SeriesContext = createContext<any>(null)

export const SeriesProvider = ({ children }) => {
  const [series, setSeries] = useState<ISeries[]>([])
  const [fetchSeries, setFetchSeries] = useState<boolean>(true)
  const { locations, allLocations } = useLocationsContext()

  useEffect(() => {
    if (!fetchSeries) return
    const effect = async () => {
      const [seriesRes, metersRes] = await Promise.all([
        API.timeSeries.fetch({
          locationPgId: -1,
          groupId: String(locations?.[1].companyPgId ?? ''),
        }),
        // Fetch meters to get isVirtual — via the shared query cache so concurrent
        // consumers of the same params (e.g. the Messstellen page) share one request.
        queryClient.fetchQuery(metersQueryOptions({ companyId: Number(locations?.[1].companyPgId) })),
      ])

      const meters = metersRes ?? []

      // The /series endpoint is expected to return an array, but can arrive as a non-array
      // (empty body, error object, or paginated wrapper). Guard to avoid crashing the provider.
      const seriesData = Array.isArray(seriesRes?.data) ? seriesRes.data : []
      const metersByDeviceId = new Map(meters.map(meter => [meter.deviceId, meter]))

      const mappedSeries = seriesData.map(serie => {
        const meter = metersByDeviceId.get(serie.id)
        const isVirtual = meter?.isVirtual ?? false
        return {
          ...serie,
          id: serie.id,
          locationId: Number(serie.locationId),
          buildingId: serie.buildingId ? Number(serie.buildingId) : serie.buildingId,
          isVirtual,
          isDefault: meter?.isDefault ?? serie.isDefault,
          deviceType: meter?.deviceType ?? serie.deviceType,
          source: meter?.source ?? serie.source,
        }
      }) as ISeries[]

      // added block to prevent wrong duplicates sent by the backend
      // NOTE: roll back this quick "patch" when the backend is fixed

      const seenIds = new Set()
      const uniqueSeries: any = []

      mappedSeries.forEach(serie => {
        const compositeId = `${serie.id}-${serie.locationId}`

        if (!seenIds.has(compositeId)) {
          seenIds.add(compositeId)
          uniqueSeries.push(serie)
        }
      })

      const labelledSeries = getLabelledTimeSeries(allLocations, uniqueSeries)

      // Filter out series with nulls in its label. This means that the user is not allowed to see this series.
      // Possibly because he has not been assigned to that location
      const allowedSeries = labelledSeries.filter(
        ({ label }) => typeof label === 'string' && label.trim().length > 0 && !label.includes('null')
      )

      setSeries(allowedSeries)
      if (fetchSeries) setFetchSeries(false)
    }

    if (locations?.[1]?.companyPgId) effect()
  }, [locations, allLocations, fetchSeries])

  return <SeriesContext.Provider value={{ series, refetch: () => setFetchSeries(true) }}>{children}</SeriesContext.Provider>
}

export const useSeriesContext = (): { series: ISeries[]; refetch: () => void } => {
  const context = useContext(SeriesContext)

  if (!context) {
    throw new Error('useSeriesContext must be used within a SeriesProvider')
  }

  return context
}

export const useOptionalSeriesContext = (): { series: ISeries[]; refetch: () => void } | null => {
  return useContext(SeriesContext)
}
